/**
 * PupRing Custom JavaScript - BOGO Module
 * ========================================
 * Handles Buy One Get One Free functionality
 *
 * Features:
 * - BOGOProgressManager class for progress bar UI
 * - BOGOUtilsAdapter class for business logic
 * - Button updates for BOGO offers
 * - Modal trigger for free item selection
 * - Free item selector visibility control
 *
 * Dependencies:
 * - window.getEligibleItemCount from custom-upsells.js
 * - CartEnhancementManager from custom-cart.js
 * - bootstrap (external)
 */

/**
 * BOGO Progress Bar Manager - FIXED VERSION
 * UI-only manager, business logic handled by BOGOUtilsAdapter
 * Fixed: Removed broken cart fetching and step calculation
 */
class BOGOProgressManager {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 3;
    this.progressBars = [];
    this.init();
  }

  static getInstance() {
    if (!window._bogoProgressManager) {
      window._bogoProgressManager = new BOGOProgressManager();
    }
    return window._bogoProgressManager;
  }

  init() {
    // Initialize all BOGO progress bars on the page
    this.progressBars = document.querySelectorAll('[data-bogo-progress-bar]');

    if (this.progressBars.length === 0) return;

    // Read initial state from first progress bar's data attributes
    const firstBar = this.progressBars[0];
    const initialStep = parseInt(firstBar.dataset.initialStep || '1', 10);
    const eligibleCount = parseInt(firstBar.dataset.eligibleCount || '0', 10);

    // Set initial step from server-side calculation
    if (initialStep && initialStep >= 1 && initialStep <= 3) {
      this.currentStep = initialStep;
      // The HTML already has correct classes, just update our state
    }

    // Listen for theme editor section reload
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.querySelector('[data-bogo-progress-bar]')) {
        this.reinit();
      }
    });

    // BOGOUtilsAdapter will control all step updates
  }

  reinit() {
    // Clean up and reinitialize for theme editor
    this.progressBars = document.querySelectorAll('[data-bogo-progress-bar]');
    // Step will be set by BOGOUtilsAdapter
  }

  setStep(step) {
    if (step < 1 || step > this.totalSteps) return;

    this.currentStep = step;

    // Re-query in case DOM changed
    this.progressBars = document.querySelectorAll('[data-bogo-progress-bar]');

    this.progressBars.forEach(progressBar => {
      // Update step buttons
      const stepButtons = progressBar.querySelectorAll('.bogo-progress-step');
      stepButtons.forEach((button, index) => {
        const buttonStep = index + 1;

        // Remove all states first
        button.classList.remove('bogo-step--active', 'bogo-step--completed');

        if (buttonStep < step) {
          // Completed steps
          button.classList.add('bogo-step--completed');
        } else if (buttonStep === step) {
          // Current step
          button.classList.add('bogo-step--active');
        }
        // Steps after current remain default (inactive)
      });

      // Update progress line
      const progressLine = progressBar.querySelector('.bogo-progress-line-fill');
      if (progressLine) {
        // Calculate percentage (0%, 50%, 100% for 3 steps)
        const percentage = ((step - 1) / (this.totalSteps - 1)) * 100;
        progressLine.style.width = `${percentage}%`;
      }
    });

    // Dispatch event for other components
    document.dispatchEvent(new CustomEvent('bogo:step-changed', {
      detail: { step: step }
    }));
  }

  // Public method to get current step
  getStep() {
    return this.currentStep;
  }

  reset() {
    this.setStep(1);
  }
}

/**
 * BOGOUtilsAdapter - Complete BOGO functionality for Ecomify theme
 * Handles button updates, state management, and modal triggers
 * Fixed version with proper selectors and observers
 */
class BOGOUtilsAdapter {
  constructor() {
    this.progressManager = BOGOProgressManager.getInstance();
    this.excludedKeywords = ['warranty', 'gift-box', 'gift box', 'engraving', 'protection'];
    this.bogoDiscountTitle = null;
    this.eligibleItemCount = 0;
    this.currentStep = 1; // Track the current BOGO step
    this.modalShown = false;
    this.cartData = null;

    // Debounce timers
    this.updateDebounceTimer = null;
    this.cartFetchTimer = null;

    // Observers
    this.buttonTextObservers = new Map();
    this.buttonDisabledObserver = null;
    this.pendingButtonUpdates = new Map();

    this.init();
  }

  static getInstance() {
    if (!window._bogoUtilsAdapter) {
      window._bogoUtilsAdapter = new BOGOUtilsAdapter();
    }
    return window._bogoUtilsAdapter;
  }

  init() {
    // Listen to cart updates (new theme events)
    window.addEventListener('updated.ecomify.cart', () => this.handleCartUpdate());

    // Listen for variant changes on product pages
    document.addEventListener('variant:change', () => this.debouncedButtonUpdate());

    // Watch for Shopify theme editor events
    document.addEventListener('shopify:section:load', () => this.reinit());
    document.addEventListener('shopify:section:unload', () => this.cleanup());

    // Handle inline free item selector add-to-cart clicks
    // FIXED: Now properly scoped to modal only
    this.initializeInlineAddToCart();

    // Initialize on page load - CRITICAL FIX
    this.initializeOnPageLoad();

    // Check sessionStorage for persisted state
    this.loadPersistedState();
  }

  /**
   * Initialize cart data and UI on page load
   * SIMPLIFIED: Immediately fetch cart and update
   */
  async initializeOnPageLoad() {
    try {
      // Immediately fetch cart data without delays
      await this.fetchCartData();

      // Update UI based on fetched cart data
      await this.handleCartUpdate();

      // Set up observers after cart data is ready
      this.observeButtons();
      this.observeButtonDisabledStates();
    } catch (error) {
      // Try to proceed with empty cart assumption
      this.cartData = { items: [], total_price: 0 };
      this.updateProgressBar();
    }
  }

  /**
   * Fetch cart data - simplified to always get fresh data
   */
  async fetchCartData() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const data = await response.json();
      this.cartData = data;
      window.cartData = data; // Share with other scripts

      return data;

    } catch (error) {
      // Return empty cart on error
      this.cartData = { items: [], total_price: 0 };
      return this.cartData;
    }
  }

  /**
   * Debounced button update to prevent rapid changes
   */
  debouncedButtonUpdate() {
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }

    this.updateDebounceTimer = setTimeout(() => {
      this.fetchCartData().then(() => {
        this.updateProductButtons();
      });
    }, 200);
  }

  /**
   * Check if an item should be excluded from BOGO
   */
  isExcludedItem(item) {
    if (!item) return true;

    const title = (item.product_title || item.title || '').toLowerCase();
    const variantTitle = (item.variant_title || '').toLowerCase();
    const properties = item.properties || {};

    // Check title and variant for excluded keywords
    for (const keyword of this.excludedKeywords) {
      if (title.includes(keyword) || variantTitle.includes(keyword)) {
        return true;
      }
    }

    // Check properties for exclusions
    for (const [key, value] of Object.entries(properties)) {
      const propKey = key.toLowerCase();
      const propValue = (value || '').toString().toLowerCase();

      if (propKey.includes('warranty') || propKey.includes('protection') ||
          propValue.includes('warranty') || propValue.includes('protection')) {
        return true;
      }
    }

    // Check for BOGO item flag
    if (properties._bogo_item === 'true') {
      return true; // Don't apply BOGO to already free items
    }

    return false;
  }

  /**
   * Get count of eligible items for BOGO
   */
  getEligibleItemCount() {
    if (!this.cartData || !this.cartData.items) return 0;

    let count = 0;
    for (const item of this.cartData.items) {
      if (!this.isExcludedItem(item)) {
        // Count quantity for proper step calculation
        count += item.quantity;
      }
    }

    return count;
  }

  /**
   * Check if cart has BOGO discount applied
   */
  hasBOGODiscount() {
    if (!this.cartData) return false;

    // Check cart-level discounts
    if (this.cartData.cart_level_discount_applications) {
      for (const discount of this.cartData.cart_level_discount_applications) {
        const title = (discount.title || '').toLowerCase();
        if (title.includes('bogo') || title.includes('buy one') || title.includes('free')) {
          this.bogoDiscountTitle = discount.title;
          return true;
        }
      }
    }

    // Check line-level discounts
    if (this.cartData.items) {
      for (const item of this.cartData.items) {
        if (item.line_level_discount_allocations) {
          for (const discount of item.line_level_discount_allocations) {
            const title = (discount.discount_application?.title || '').toLowerCase();
            if (title.includes('bogo') || title.includes('buy one') || title.includes('free')) {
              this.bogoDiscountTitle = discount.discount_application?.title;
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * Update product page buttons based on BOGO eligibility
   * FIXED: Shows "ADD ITEM FOR FREE" when customer has exactly 1 eligible item
   */
  updateProductButtons() {
    const eligibleCount = this.getEligibleItemCount();

    // Find all add to cart buttons - FIXED SELECTORS
    const buttons = document.querySelectorAll(
      '.btn-atc, button[name="add"], .product-form__submit, .btn-add-to-cart'
    );

    buttons.forEach(button => {
      try {
        // Skip if button is in cart or modal
        if (button.closest('.offcanvas-cart') || button.closest('#bogo-recommendation-modal')) {
          return;
        }

        // Check if button is disabled (for personalized products)
        if (button.disabled) {
          // Queue update for when button is enabled
          if (eligibleCount === 1) {
            this.pendingButtonUpdates.set(button, true);
          } else {
            this.pendingButtonUpdates.delete(button);
          }
          return;
        }

        // Skip if button is for excluded products
        const form = button.closest('form');
        const productTitle = form?.querySelector('[data-product-title], .product-title, h1, h2')?.textContent || '';

        if (this.isExcludedByTitle(productTitle)) {
          this.resetButton(button);
          return;
        }

        // FIXED LOGIC: Show FREE button when customer has exactly 1 eligible item
        if (eligibleCount === 1) {
          // Customer has 1 item, next item will be free
          this.updateButtonForFree(button);
          this.observeButtonTextChanges(button);
        } else {
          this.resetButton(button);
          this.unobserveButtonTextChanges(button);
        }
      } catch (error) {
        // Error updating button - continue
      }
    });

    // Save state
    this.persistState();
  }

  /**
   * Check if product title indicates exclusion
   */
  isExcludedByTitle(title) {
    const lowerTitle = title.toLowerCase();
    for (const keyword of this.excludedKeywords) {
      if (lowerTitle.includes(keyword)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Update button to show BOGO offer
   * FIXED: Now targets correct elements in Ecomify theme
   */
  updateButtonForFree(button) {
    if (!button) return;

    // Store original text if not already stored
    const textElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;

    if (!button.dataset.originalText) {
      button.dataset.originalText = textElement.textContent.trim();
    }

    // Update button text
    textElement.innerHTML = '🎁 ADD ITEM FOR FREE';
    button.classList.add('bogo-available');

    // Add pulse animation
    button.style.animation = 'bogo-pulse 2s infinite';

    // Mark as BOGO button
    button.dataset.bogoActive = 'true';
  }

  /**
   * Reset button to original state
   */
  resetButton(button) {
    if (!button) return;

    if (button.dataset.originalText) {
      const textElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;
      textElement.textContent = button.dataset.originalText;
      delete button.dataset.originalText;
    }

    button.classList.remove('bogo-available');
    button.style.animation = '';
    delete button.dataset.bogoActive;
  }

  /**
   * Observe button text changes to maintain BOGO text
   * FIXED: Handles innerHTML replacement by theme
   */
  observeButtonTextChanges(button) {
    if (!button || this.buttonTextObservers.has(button)) return;

    const observer = new MutationObserver((mutations) => {
      // Check if button should show BOGO text
      if (button.dataset.bogoActive === 'true') {
        // Re-query for text element as it may have been replaced
        const textElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;

        // Check if text was changed away from BOGO
        if (!textElement.innerHTML.includes('🎁')) {
          // Restore BOGO text with slight delay to ensure it sticks
          setTimeout(() => {
            const currentTextElement = button.querySelector('.btn-atc-text, .btn-text, span') || button;
            currentTextElement.innerHTML = '🎁 ADD ITEM FOR FREE';
          }, 10);
        }
      }
    });

    // Observe the button itself for any changes
    observer.observe(button, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['innerHTML']
    });

    this.buttonTextObservers.set(button, observer);
  }

  /**
   * Stop observing button text changes
   */
  unobserveButtonTextChanges(button) {
    const observer = this.buttonTextObservers.get(button);
    if (observer) {
      observer.disconnect();
      this.buttonTextObservers.delete(button);
    }
  }

  /**
   * Watch for button disabled state changes
   * NEW: Handles personalized products that start disabled
   */
  observeButtonDisabledStates() {
    if (this.buttonDisabledObserver) return;

    this.buttonDisabledObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          const button = mutation.target;

          // Check if button just became enabled and has pending update
          if (!button.disabled && this.pendingButtonUpdates.has(button)) {
            const shouldUpdate = this.pendingButtonUpdates.get(button);
            this.pendingButtonUpdates.delete(button);

            if (shouldUpdate) {
              setTimeout(() => this.updateButtonForFree(button), 50);
            }
          }
        }
      });
    });

    // Observe all buttons for disabled attribute changes
    const buttons = document.querySelectorAll('.btn-atc, button[name="add"]');
    buttons.forEach(button => {
      this.buttonDisabledObserver.observe(button, {
        attributes: true,
        attributeFilter: ['disabled']
      });
    });
  }

  /**
   * Watch for dynamic button rendering
   * ENHANCED: More robust observation
   */
  observeButtons() {
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach(mutation => {
        // Check if buttons were added
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.matches && (node.matches('.btn-atc, button[name="add"]') ||
                node.querySelector('.btn-atc, button[name="add"]'))) {
              shouldUpdate = true;

              // Also observe new buttons for disabled state
              const newButtons = node.matches('.btn-atc, button[name="add"]') ?
                [node] : node.querySelectorAll('.btn-atc, button[name="add"]');

              newButtons.forEach(btn => {
                if (this.buttonDisabledObserver) {
                  this.buttonDisabledObserver.observe(btn, {
                    attributes: true,
                    attributeFilter: ['disabled']
                  });
                }
              });
            }
          }
        });
      });

      if (shouldUpdate) {
        this.debouncedButtonUpdate();
      }
    });

    // Observe main containers for button additions
    const containers = [
      document.body, // Catch all dynamic content
      ...document.querySelectorAll('.product-form, .card-product, .product-item, main')
    ];

    containers.forEach(container => {
      if (container) {
        observer.observe(container, {
          childList: true,
          subtree: true
        });
      }
    });

    // Store observer for cleanup
    this.domObserver = observer;
  }

  /**
   * Handle cart update events
   * FIXED: Correct step calculation based on eligible items only
   */
  async handleCartUpdate() {
    try {
      // Fetch latest cart data
      await this.fetchCartData();

      // Update eligible count
      this.eligibleItemCount = this.getEligibleItemCount();

      // Update BOGO presence flag
      this.hasBogoProducts = this.checkHasBOGOItems();

      // Update buttons with debounce
      this.debouncedButtonUpdate();

      // Update progress bar
      this.updateProgressBar();

      // Persist state
      this.persistState();
    } catch (error) {
      // Error handling cart update - continue
    }
  }

  /**
   * Update progress bar based on eligible item count
   * FIXED: Correct logic for steps
   * Step 1: 0 eligible items (Design 1st Pair)
   * Step 2: 1 eligible item (Select FREE Item)
   * Step 3: 2+ eligible items (Finish Your Order)
   */
  updateProgressBar() {
    // Simplified step calculation
    let step = 1; // Default: No items (Add 2 items for BOGO)

    if (this.eligibleItemCount === 0) {
      step = 1; // No eligible items - start shopping
    } else if (this.eligibleItemCount === 1 && !this.hasBogoProducts) {
      step = 2; // 1 item, no BOGO - select free item
    } else if (this.eligibleItemCount >= 1) {
      step = 3; // Has items and/or BOGO - checkout ready
    }

    // Store the current step
    this.currentStep = step;

    // Update progress bar UI
    if (this.progressManager && this.progressManager.setStep) {
      this.progressManager.setStep(step);
    }
  }

  /**
   * Check if cart has any BOGO items
   */
  checkHasBOGOItems() {
    if (!this.cartData || !this.cartData.items) return false;

    const hasBogo = this.cartData.items.some(item => {
      const properties = item.properties || {};
      const isBogo = properties._bogo_item === 'true';
      return isBogo;
    });

    return hasBogo;
  }

  /**
   * Check and show BOGO modal if conditions are met
   */
  checkAndShowBOGOModal() {
    // Check if modal was shown this session (matches old implementation)
    const modalShown = sessionStorage.getItem('bogoModalShown');

    if (modalShown) {
      return; // Already shown this session
    }

    // Show the modal after a short delay (300ms matches old theme)
    setTimeout(() => {
      const modalElement = document.getElementById('bogo-recommendation-modal');

      if (!modalElement || typeof bootstrap === 'undefined') {
        return;
      }

      try {
        const modalInstance = new bootstrap.Modal(modalElement);
        modalInstance.show();
        // Mark as shown for this session
        sessionStorage.setItem('bogoModalShown', 'true');
      } catch (error) {
        // Error showing modal - silently fail
      }
    }, 300);
  }

  /**
   * Clear BOGO modal session for testing
   * Can be called from console: BOGOUtilsAdapter.getInstance().clearBOGOModal()
   */
  clearBOGOModal() {
    sessionStorage.removeItem('bogoModalShown');

    // Optionally trigger immediate check
    if (this.currentStep === 2) {
      this.checkAndShowBOGOModal();
    }
  }

  /**
   * Check if free item selector should be displayed
   * Shows when customer has exactly 1 eligible item and hasn't dismissed today
   */
  checkAndDisplayFreeItemSelector() {
    // Check if selector is enabled
    const selectors = document.querySelectorAll('.free-item-selector');
    if (selectors.length === 0) return;

    // Check if dismissed today
    if (this.isDismissedToday()) {
      this.hideFreeItemSelector();
      return;
    }

    // Show if customer has exactly 1 eligible item
    if (this.eligibleItemCount === 1) {
      this.showFreeItemSelector();
    } else {
      this.hideFreeItemSelector();
    }
  }

  /**
   * Check if selector was dismissed today (24-hour window)
   */
  isDismissedToday() {
    const dismissedUntil = localStorage.getItem('bogo_selector_dismissed_until');
    if (!dismissedUntil) return false;

    const dismissTime = parseInt(dismissedUntil);
    const now = Date.now();

    // If more than 24 hours have passed, clear the flag
    if (now > dismissTime) {
      localStorage.removeItem('bogo_selector_dismissed_until');
      return false;
    }

    return true;
  }

  /**
   * Backward compatibility wrapper
   */
  checkStep2Eligibility() {
    this.checkAndDisplayFreeItemSelector();
  }

  /**
   * Show free item selector inline in cart
   */
  showFreeItemSelector() {
    // Find all selector instances (drawer and page)
    const selectors = document.querySelectorAll('.free-item-selector');
    selectors.forEach(selector => {
      if (selector) {
        selector.style.display = 'block';
        // Add animation class if desired
        selector.classList.add('fade-in');
      }
    });
  }

  /**
   * Hide free item selector
   */
  hideFreeItemSelector() {
    const selectors = document.querySelectorAll('.free-item-selector');
    selectors.forEach(selector => {
      if (selector) {
        selector.style.display = 'none';
      }
    });
  }

  /**
   * Dismiss free item selector for 24 hours
   */
  dismissFreeItemSelectorForToday() {
    // Hide the selector
    this.hideFreeItemSelector();

    // Set dismissal timestamp (24 hours from now)
    const dismissUntil = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem('bogo_selector_dismissed_until', dismissUntil.toString());
  }

  /**
   * Dismiss free item selector (backward compatibility)
   */
  dismissFreeItemSelector() {
    this.dismissFreeItemSelectorForToday();
  }

  /**
   * Initialize inline add-to-cart functionality
   * FIXED: Scoped to modal only, not entire document
   */
  initializeInlineAddToCart() {
    // Only add listener to the modal itself, not the entire document
    const modal = document.getElementById('bogo-recommendation-modal');

    // Also check for inline selectors (though they're disabled)
    const selectors = document.querySelectorAll('.free-item-selector');

    // Add listener to modal if it exists
    if (modal) {
      modal.addEventListener('click', async (e) => {
        // Check if clicked element itself has the attribute (not using closest)
        if (e.target && e.target.hasAttribute('data-bogo-add-inline')) {
          e.preventDefault();
          await this.handleInlineAddToCart(e.target);
        }
      });
    }

    // Add listeners to inline selectors if they exist (though they're hidden)
    selectors.forEach(selector => {
      selector.addEventListener('click', async (e) => {
        if (e.target && e.target.hasAttribute('data-bogo-add-inline')) {
          e.preventDefault();
          await this.handleInlineAddToCart(e.target);
        }
      });
    });
  }

  /**
   * Handle inline add-to-cart for free item selector
   */
  async handleInlineAddToCart(button) {
    try {
      // Disable button and show loading state
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Loading...';

      // Get product data from button attributes
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      const productHandle = button.dataset.productHandle;
      const productUrl = button.dataset.productUrl;

      // Must have URL or handle to redirect
      if (!productUrl && !productHandle) {
        throw new Error('No product URL available');
      }

      // Construct the product page URL with BOGO context
      const targetUrl = productUrl || `/products/${productHandle}`;

      // Add BOGO parameters to preserve context
      const url = new URL(targetUrl, window.location.origin);
      url.searchParams.set('bogo', 'true');
      url.searchParams.set('step', '2');
      url.searchParams.set('from', 'inline');

      // Store BOGO context in session for reliability
      sessionStorage.setItem('bogo_redirect_context', JSON.stringify({
        step: 2,
        timestamp: Date.now(),
        fromInline: true,
        productHandle: productHandle,
        variantId: variantId
      }));

      // Close the cart drawer before redirecting
      const cartOffcanvas = document.getElementById('offcanvas-cart');
      if (cartOffcanvas && typeof bootstrap !== 'undefined') {
        const offcanvasInstance = bootstrap.Offcanvas.getInstance(cartOffcanvas);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Small delay to ensure cart closes smoothly
      setTimeout(() => {
        // Redirect to product page for customization
        window.location.href = url.toString();
      }, 200);

    } catch (error) {
      // Reset button on error
      button.disabled = false;
      button.textContent = originalText;

      // Show error message
      alert('Unable to load product. Please refresh and try again.');
    }
  }

  /**
   * Refresh cart drawer/page
   */
  async refreshCart() {
    try {
      // Create a fake successful response object for refreshCartContents
      const fakeResponse = {
        ok: true,
        url: '/cart/add.js'
      };

      // Use the theme's built-in cart refresh function
      if (window.refreshCartContents) {
        await window.refreshCartContents(fakeResponse);
      } else {
        // Fallback: trigger manual refresh
        document.dispatchEvent(new CustomEvent('updated.ecomify.cart'));
      }

      // Fetch updated cart data for our BOGO logic
      await this.fetchCartData();

      // Update BOGO state
      await this.handleCartUpdate();
    } catch (error) {
      // Error refreshing cart - fallback to manual event
      document.dispatchEvent(new CustomEvent('updated.ecomify.cart'));
    }
  }

  /**
   * Save state to sessionStorage
   */
  persistState() {
    const state = {
      eligibleItemCount: this.eligibleItemCount,
      bogoDiscountTitle: this.bogoDiscountTitle,
      currentStep: this.progressManager?.currentStep || 1,
      timestamp: Date.now()
    };

    sessionStorage.setItem('bogoState', JSON.stringify(state));
  }

  /**
   * Load state from sessionStorage
   */
  loadPersistedState() {
    const savedState = sessionStorage.getItem('bogoState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);

        // Check if state is less than 5 minutes old
        if (state.timestamp && (Date.now() - state.timestamp) < 300000) {
          this.eligibleItemCount = state.eligibleItemCount || 0;
          this.bogoDiscountTitle = state.bogoDiscountTitle || null;

          // Update UI based on loaded state
          this.debouncedButtonUpdate();
        }
      } catch (e) {
        // Failed to load state - continue with fresh state
      }
    }
  }

  /**
   * Cleanup observers and timers
   */
  cleanup() {
    // Clear timers
    if (this.updateDebounceTimer) clearTimeout(this.updateDebounceTimer);
    if (this.cartFetchTimer) clearTimeout(this.cartFetchTimer);

    // Disconnect observers
    this.buttonTextObservers.forEach(observer => observer.disconnect());
    this.buttonTextObservers.clear();

    if (this.buttonDisabledObserver) {
      this.buttonDisabledObserver.disconnect();
      this.buttonDisabledObserver = null;
    }

    if (this.domObserver) {
      this.domObserver.disconnect();
      this.domObserver = null;
    }

    // Clear pending updates
    this.pendingButtonUpdates.clear();
  }

  /**
   * Reinitialize after theme editor changes
   */
  reinit() {
    this.cleanup();
    setTimeout(() => this.init(), 100);
  }

  /**
   * Public method to manually trigger update
   */
  refresh() {
    this.fetchCartData().then(() => this.handleCartUpdate());
  }

  /**
   * Destroy instance (for cleanup)
   */
  destroy() {
    this.cleanup();
    window._bogoUtilsAdapter = null;
  }
}

// Initialize both managers when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    BOGOProgressManager.getInstance();
    const bogoAdapter = BOGOUtilsAdapter.getInstance();
    // Make available globally for debugging
    window.BOGOAdapter = bogoAdapter;
    window.clearBOGOModal = () => bogoAdapter.clearBOGOModal();
  });
} else {
  BOGOProgressManager.getInstance();
  const bogoAdapter = BOGOUtilsAdapter.getInstance();
  // Make available globally for debugging
  window.BOGOAdapter = bogoAdapter;
  window.clearBOGOModal = () => bogoAdapter.clearBOGOModal();
}

// Export for use by other modules
window.BOGOProgressManager = BOGOProgressManager;
window.BOGOUtilsAdapter = BOGOUtilsAdapter;
